import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Syncs a state value with a URL search parameter bidirectionally.
 *
 * - Initializes state from URL on mount
 * - Syncs state changes back to URL (with replace: true to avoid navigation issues)
 * - Deletes the param when value is empty
 *
 * @param paramName - The URL search parameter name
 * @param initialValue - The initial state value
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useUrlState("search", { initialValue: "" });
 * ```
 */
export const useUrlState = <T extends string | undefined>(
  paramName: string,
  {
    initialValue,
  }: {
    initialValue: T;
  },
) => {
  const [value, setValue] = useState<T>(initialValue);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearchParamsRef = useRef(searchParams);
  const isMountedRef = useRef(false);

  useEffect(() => {
    const valueFromUrl = initialSearchParamsRef.current.get(paramName) ?? "";
    setValue(valueFromUrl as T);
  }, [paramName, setValue]);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    setSearchParams(
      (prev) => {
        const currentUrlValue = prev.get(paramName) ?? "";

        if (value !== currentUrlValue) {
          const newParams = new URLSearchParams(prev);
          if (value) {
            newParams.set(paramName, String(value));
          } else {
            newParams.delete(paramName);
          }
          return newParams;
        }
        return prev;
      },
      { replace: true },
    );
  }, [value, paramName, setSearchParams]);

  return [value, setValue] as const;
};
